import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover } from '@headlessui/react';
import { SelectorIcon as ChevronUpDownIcon } from '@heroicons/react/solid';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollContainer } from 'react-indiana-drag-scroll';
import invariant from 'tiny-invariant';
import { CombinedError, RequestPolicy, useQuery } from 'urql';
import {
  ListAllAssetsDocument,
  ListAllAssetsQuery,
  ListAssetsDocument,
  ListAssetsQuery,
} from '~/generated/graphql';
import { graphql } from '~/gql';
import { unique } from '~/helpers/array';
import { notUndefined } from '~/helpers/filter';
import { Combolist } from '../Combolist';
import { styles } from '../FindAddContact/ContactSelect';
import { SearchInput } from '../FindAddContact/SearchInput';
import { useSites } from '../FindAddSite/FindAddSite';
import { PopoverStateChangedEffect } from '../PopoverStateChangedEffect';
import { Avatar } from '../ui/Avatar';
import { Filter, FilterGroup } from '../ui/FilterGroup';
import { Label } from '../ui/Label';
import { MenuTransition } from '../ui/Transition/MenuTransition';

export type Asset = ListAssetsQuery['assets'][number];
export type AllAsset = ListAllAssetsQuery['assets'][number];

export type Props = {
  assets: Asset[];
  value: Asset[];
  onChange: (selected: Asset[]) => void;
  multiple?: boolean;

  // Filter-related props
  enableFilters?: boolean;
  defaultLocationId?: string;
  mode?: 'specific' | 'all-matching';

  // Filter state management
  onFiltersChange?: (filters: {
    places: string[];
    spaces: string[];
    assetTypes: string[];
  }) => void;

  // Error handling
  error?: string;
};

export const DeploymentFieldsFragment = graphql(`
  fragment DeploymentFields on Deployment {
    id
    displayLocation
    startedAt
    image
    location {
      __typename
      id
      name
    }
    space {
      id
      name
    }
  }
`);

export function useAssets({
  requestPolicy = 'cache-first',
  pause = false,
}: {
  requestPolicy?: RequestPolicy;
  pause?: boolean;
} = {}): [Asset[], () => void, boolean, CombinedError | undefined] {
  const [result, reexecuteQuery] = useQuery({
    query: ListAssetsDocument,
    variables: {},
    requestPolicy,
    pause,
  });

  return [
    result.data?.assets ?? [],
    () => reexecuteQuery({ requestPolicy: 'network-only' }),
    result.fetching,
    result.error,
  ];
}

export function useAllAssets({
  requestPolicy = 'cache-first',
  pause = false,
}: {
  requestPolicy?: RequestPolicy;
  pause?: boolean;
} = {}): [AllAsset[], () => void, boolean, CombinedError | undefined] {
  const [result, reexecuteQuery] = useQuery({
    query: ListAllAssetsDocument,
    variables: {},
    requestPolicy,
    pause,
    context: useMemo(() => ({}), []),
  });

  const reload = useCallback(() => {
    reexecuteQuery({ requestPolicy: 'network-only' });
  }, [reexecuteQuery]);

  return [result.data?.assets ?? [], reload, result.fetching, result.error];
}

/**
 * Asset select
 */
export function AssetSelect({
  assets,
  value,
  onChange,
  multiple = false,
  enableFilters = false,
  defaultLocationId,
  mode = 'specific',
  onFiltersChange,
  error,
}: Props) {
  const { t } = useTranslation();
  const [cancelling, setCancelling] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter state using URLSearchParams (like FilterGroup)
  const [filterValue, setFilterValue] = useState(() => {
    const params = new URLSearchParams();
    if (enableFilters && defaultLocationId) {
      params.append('places', defaultLocationId);
    }
    return params;
  });

  const [sites] = useSites();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // Scroll into view when popover opens
  useEffect(() => {
    if (isPopoverOpen && containerRef.current) {
      // Small delay to ensure popover is rendered
      const timeoutId = setTimeout(() => {
        containerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }, 100);

      // Cleanup timeout if component unmounts or effect re-runs
      return () => clearTimeout(timeoutId);
    }
  }, [isPopoverOpen]);

  // Scroll into view when new assets are added to keep bottom buttons visible
  useEffect(() => {
    if (isPopoverOpen && containerRef.current && value.length > 0) {
      // Small delay to ensure new assets are rendered
      const timeoutId = setTimeout(() => {
        containerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }, 100);

      // Cleanup timeout if component unmounts or effect re-runs
      return () => clearTimeout(timeoutId);
    }
  }, [value.length, isPopoverOpen]);

  // ============================================================================
  // CASCADING FILTER LOGIC
  // ============================================================================
  // The filter cascade works as follows:
  // 1. Places (Sites) - Always shows all available sites
  // 2. Asset Types - Filtered by selected places
  // 3. Spaces - Filtered by both selected places AND asset types
  // ============================================================================

  /**
   * Gets all unique spaces from assets in the specified places
   * @param selectedPlaces Array of place IDs to filter by (empty = all places)
   * @returns Sorted array of unique space names
   */
  const getAvailableSpacesForPlaces = useCallback(
    (selectedPlaces: string[]) => {
      // If no places selected, return all spaces across all assets
      if (selectedPlaces.length === 0) {
        return unique(
          assets
            .map((asset) => asset.deployment?.space.name)
            .filter(notUndefined)
        ).sort();
      }

      // Filter assets by selected places first, then extract spaces
      const assetsInSelectedPlaces = assets.filter((asset) => {
        const deployment = asset.deployment;
        return deployment && selectedPlaces.includes(deployment.location.id);
      });

      return unique(
        assetsInSelectedPlaces
          .map((asset) => asset.deployment?.space.name)
          .filter(notUndefined)
      ).sort();
    },
    [assets]
  );

  /**
   * Gets all unique asset types from assets in the specified places
   * @param selectedPlaces Array of place IDs to filter by (empty = all places)
   * @returns Sorted array of unique asset type names
   */
  const getAvailableTypesForPlaces = useCallback(
    (selectedPlaces: string[]) => {
      // If no places selected, return all asset types
      if (selectedPlaces.length === 0) {
        return unique(assets.map((asset) => asset.type))
          .filter(Boolean)
          .sort();
      }

      // Filter assets by selected places first, then extract types
      const assetsInSelectedPlaces = assets.filter((asset) => {
        const deployment = asset.deployment;
        return deployment && selectedPlaces.includes(deployment.location.id);
      });

      return unique(assetsInSelectedPlaces.map((asset) => asset.type))
        .filter(Boolean)
        .sort();
    },
    [assets]
  );

  /**
   * Gets all unique spaces from assets matching both places and asset types
   * This is the final step in the cascade: Places → Types → Spaces
   * @param selectedPlaces Array of place IDs to filter by
   * @param selectedTypes Array of asset types to filter by
   * @returns Sorted array of unique space names that contain the specified asset types in the specified places
   */
  const getAvailableSpacesForPlacesAndTypes = useCallback(
    (selectedPlaces: string[], selectedTypes: string[]) => {
      // Filter assets by both places and types, then extract spaces
      const matchingAssets = assets.filter((asset) => {
        const deployment = asset.deployment;
        if (!deployment) return false;

        // Check if asset is in one of the selected places (or no places filter)
        const placeMatch =
          selectedPlaces.length === 0 ||
          selectedPlaces.includes(deployment.location.id);

        // Check if asset is one of the selected types (or no types filter)
        const typeMatch =
          selectedTypes.length === 0 || selectedTypes.includes(asset.type);

        return placeMatch && typeMatch;
      });

      return unique(
        matchingAssets
          .map((asset) => asset.deployment?.space.name)
          .filter(notUndefined)
      ).sort();
    },
    [assets]
  );

  // Auto-populate site filter with defaultLocationId when it changes
  // but don't override if user has manually selected a different site
  useEffect(() => {
    if (!enableFilters || !defaultLocationId) return;

    const currentPlaces = filterValue.getAll('places');

    // Only auto-populate if no places are selected yet
    if (currentPlaces.length === 0) {
      setFilterValue((prev) => {
        const newFilterValue = new URLSearchParams(prev);
        newFilterValue.set('places', defaultLocationId);
        return newFilterValue;
      });
    }
  }, [defaultLocationId, enableFilters]); // Safe deps since we check conditions inside
  // Note: filterValue is intentionally excluded to prevent infinite loops

  // Handle cascading filter changes through FilterGroup's onChange
  const handleFilterChange = useCallback(
    (newFilterValue: URLSearchParams) => {
      const oldPlaces = filterValue.getAll('places');
      const newPlaces = newFilterValue.getAll('places');
      const oldTypes = filterValue.getAll('assetTypes');
      const newTypes = newFilterValue.getAll('assetTypes');

      // Check if places or types changed
      const placesChanged =
        [...oldPlaces].sort().join(',') !== [...newPlaces].sort().join(',');
      const typesChanged =
        [...oldTypes].sort().join(',') !== [...newTypes].sort().join(',');

      if (placesChanged || typesChanged) {
        // Apply cascading logic
        const finalFilterValue = new URLSearchParams();

        // Keep places as-is
        newPlaces.forEach((place) => finalFilterValue.append('places', place));

        // Filter asset types to only those available in selected places
        const availableTypes = getAvailableTypesForPlaces(newPlaces);
        const validTypes = newTypes.filter((type) =>
          availableTypes.includes(type)
        );
        validTypes.forEach((type) =>
          finalFilterValue.append('assetTypes', type)
        );

        // Filter spaces to only those available in selected places and types
        const availableSpaces = getAvailableSpacesForPlacesAndTypes(
          newPlaces,
          validTypes
        );
        const currentSpaces = newFilterValue.getAll('spaces');
        const validSpaces = currentSpaces.filter((space) =>
          availableSpaces.includes(space)
        );
        validSpaces.forEach((space) =>
          finalFilterValue.append('spaces', space)
        );

        setFilterValue(finalFilterValue);
      } else {
        // No cascading needed, just update normally
        setFilterValue(newFilterValue);
      }
    },
    [
      filterValue, // Required for comparison logic
      getAvailableTypesForPlaces, // Stable memoized function
      getAvailableSpacesForPlacesAndTypes, // Stable memoized function
    ]
    // Note: These helper functions are memoized with [assets] dependency,
    // so they're stable unless assets change, which is appropriate
  );

  // Notify parent of filter changes
  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange({
        places: filterValue.getAll('places'),
        spaces: filterValue.getAll('spaces'),
        assetTypes: filterValue.getAll('assetTypes'),
      });
    }
  }, [filterValue, onFiltersChange]); // Both dependencies are necessary and stable

  // Generate filter options with cascading logic
  const filterOptions = useMemo(() => {
    if (!enableFilters) return [];

    const filters: Filter[] = [];
    const selectedPlaces = filterValue.getAll('places');
    const selectedTypes = filterValue.getAll('assetTypes');

    // Places filter - always show all sites
    if (sites.length > 0) {
      filters.push({
        name: 'places',
        label: 'Places',
        type: 'select' as const,
        multiple: true,
        searchable: true,
        options: sites.map((site) => ({
          value: site.id,
          label: site.name,
        })),
      });
    }

    // Asset types filter - show types available in selected places
    const availableTypes = getAvailableTypesForPlaces(selectedPlaces);
    if (availableTypes.length > 0) {
      filters.push({
        name: 'assetTypes',
        label: 'Asset Types',
        type: 'select' as const,
        multiple: true,
        searchable: true,
        options: availableTypes.map((type) => ({
          value: type,
          label: type,
        })),
      });
    }

    // Spaces filter - show spaces available in selected places and types
    const availableSpaces = getAvailableSpacesForPlacesAndTypes(
      selectedPlaces,
      selectedTypes
    );
    if (availableSpaces.length > 0) {
      filters.push({
        name: 'spaces',
        label: 'Spaces',
        type: 'select' as const,
        multiple: true,
        searchable: true,
        options: availableSpaces.map((space) => ({
          value: space,
          label: space,
        })),
      });
    }

    return filters;
  }, [
    sites,
    enableFilters,
    filterValue,
    getAvailableTypesForPlaces,
    getAvailableSpacesForPlacesAndTypes,
  ]);

  // Apply filters to assets
  const filteredAssets = useMemo(() => {
    let filtered = assets;

    // Apply search filter first
    if (query) {
      filtered = filtered.filter(
        (asset) =>
          asset.name.toLowerCase().includes(query.toLowerCase()) ||
          asset.type.toLowerCase().includes(query.toLowerCase())
      );
    }

    if (!enableFilters) return filtered;

    return filtered.filter((asset) => {
      const deployment = asset.deployment;

      // POTENTIAL ISSUE: Assets without deployments are filtered out
      // This might be intentional, but could cause confusion if undeployed assets should be selectable
      if (!deployment) return false;

      // Places filter
      const selectedPlaces = filterValue.getAll('places');
      const placeMatch =
        !selectedPlaces.length ||
        selectedPlaces.includes(deployment.location.id);

      // Spaces filter
      const selectedSpaces = filterValue.getAll('spaces');
      const spaceMatch =
        !selectedSpaces.length ||
        selectedSpaces.includes(deployment.space.name);

      // Asset types filter
      const selectedTypes = filterValue.getAll('assetTypes');
      const typeMatch =
        !selectedTypes.length || selectedTypes.includes(asset.type);

      // Mode-specific logic
      if (mode === 'all-matching') {
        // IMPORTANT: For all-matching mode, require at least asset types to be selected
        // This ensures users specify what type of assets they want to include
        const hasRequiredFilters = selectedTypes.length > 0;
        return hasRequiredFilters && placeMatch && spaceMatch && typeMatch;
      } else {
        // Optional filters for specific mode - show all assets if no filters
        return placeMatch && spaceMatch && typeMatch;
      }
    });
  }, [assets, query, filterValue, enableFilters, mode]);

  // Include selected assets even if they don't match current filters (for specific mode)
  const availableAssets = useMemo(() => {
    // Start with filtered assets
    const availableAssetsMap = new Map();

    // First add filtered assets with location grouping
    filteredAssets.forEach((asset) => {
      const assetWithGroup = {
        ...asset,
        $group: asset.deployment?.location.name || 'No Location',
      };
      availableAssetsMap.set(asset.id, assetWithGroup);
    });

    // Then add selected assets with location grouping (ensures they're always available)
    // This prevents selections from disappearing when filters change
    value.forEach((asset) => {
      const assetWithGroup = {
        ...asset,
        $group: asset.deployment?.location.name || 'No Location',
      };
      availableAssetsMap.set(asset.id, assetWithGroup);
    });

    return Array.from(availableAssetsMap.values());
  }, [filteredAssets, value]);

  const handleClosed = () => {
    if (cancelling) {
      setCancelling(false);
      return;
    }
    // No need to call onChange since we're using value prop directly
  };

  const handleRemove = (asset: Asset) => {
    const newValue = value.filter(({ id }) => id !== asset.id);
    onChange(newValue);
  };

  return (
    <>
      <Label>{t('asset', { count: value.length })}</Label>
      {/* Raise z-index on the trigger container so the dropdown can sit above surrounding sections */}
      <Popover className='relative z-auto' ref={containerRef}>
        <SelectButton
          selected={value}
          error={Boolean(error)}
          onRemove={handleRemove}
        />
        {error && <p className='ErrorMessage'>{error}</p>}
        <MenuTransition>
          {/* Standard Popover.Panel anchored to the trigger, with high z-index so it appears above nearby sections */}
          <Popover.Panel
            className={classNames(
              'absolute top-full z-[1100] mt-1 w-full rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm',
              multiple && 'pb-0'
            )}
          >
            {({ open }) => {
              // Track open state for scroll-into-view behaviour
              useEffect(() => {
                if (open !== isPopoverOpen) {
                  setIsPopoverOpen(open);
                }
              }, [open, isPopoverOpen]);

              return (
                <>
                  <PopoverStateChangedEffect
                    open={open}
                    onClose={handleClosed}
                  />

                  {/* Sticky Filter Bar - Following SiteSelect pattern */}
                  <div className='sticky -top-1 z-50 -mt-1 mb-1 bg-white p-1 pb-0'>
                    <SearchInput
                      className='rounded-full border-none bg-grey-3'
                      inputProps={{ className: 'bg-grey-3' }}
                      placeholder={t('asset_plural')}
                      setQuery={setQuery}
                      query={query}
                    />

                    {/* Filter Pills - Horizontal scrollable like SiteSelect */}
                    {enableFilters && filterOptions.length > 0 && (
                      <ScrollContainer
                        mouseScroll={{
                          ignoreElements: '.prevent-drag-scroll',
                        }}
                        className='scrollbar-hide mt-2'
                      >
                        <div className='flex w-full items-center gap-2 pb-1'>
                          <FilterGroup
                            filters={filterOptions}
                            value={filterValue}
                            onChange={handleFilterChange}
                            placement='portal'
                          />
                        </div>
                      </ScrollContainer>
                    )}

                    {/* Preview count for all-matching mode */}
                    {mode === 'all-matching' && enableFilters && (
                      <div className='mt-2 text-sm text-secondary'>
                        {filteredAssets.length} asset
                        {filteredAssets.length !== 1 ? 's' : ''} match your
                        criteria
                      </div>
                    )}
                  </div>

                  {/* Asset List */}
                  <div className='overflow-y-auto'>
                    <Combolist
                      items={availableAssets}
                      searchKeys={['name', 'type']}
                      placeholder={t('asset_plural')}
                      value={value}
                      onChange={onChange}
                      multiple={multiple}
                      searchable={false} // We handle search above
                      renderEmpty={() =>
                        enableFilters ? (
                          <>No assets match your current filters</>
                        ) : (
                          <>No assets deployed at this location</>
                        )
                      }
                      renderInner={(asset) => <OptionInner asset={asset} />}
                    />
                  </div>

                  {/* Footer */}
                  {multiple && (
                    <div className='z-10 flex border-t bg-white p-1.5 text-xs'>
                      <Popover.Button
                        className='flex-1 p-2 text-left uppercase'
                        onClick={() => setCancelling(true)}
                      >
                        {t('cancel')}
                      </Popover.Button>
                      <Popover.Button className='flex-1 p-2 text-right uppercase'>
                        {t('ok')}
                      </Popover.Button>
                    </div>
                  )}
                </>
              );
            }}
          </Popover.Panel>
        </MenuTransition>
      </Popover>
    </>
  );
}

function SelectButton({
  selected,
  error,
  onRemove,
}: {
  selected: Asset[];
  error?: boolean;
  onRemove: (asset: Asset) => void;
}) {
  // Group selected assets by location
  const groupedAssets = useMemo(() => {
    const groups: { [locationName: string]: Asset[] } = {};

    selected.forEach((asset) => {
      const locationName = asset.deployment?.location.name || 'No Location';
      if (!groups[locationName]) {
        groups[locationName] = [];
      }
      groups[locationName].push(asset);
    });

    return groups;
  }, [selected]);

  return (
    <Popover.Button
      as='div'
      className={classNames(styles.control, 'py-2.5', {
        'border-red-500': error,
      })}
    >
      <div className='flex items-center'>
        <div className='flex max-w-full flex-1 flex-col gap-2 text-left'>
          {Object.entries(groupedAssets).map(([locationName, assets]) => (
            <div key={locationName} className='flex flex-col gap-1'>
              {/* Location Header */}
              <div className='text-grey-60 text-sm font-medium tracking-wide'>
                {locationName}
              </div>
              {/* Assets in this location */}
              {assets.map((asset) => (
                <Inner key={asset.id} asset={asset} onRemove={onRemove} />
              ))}
            </div>
          ))}
        </div>
        <ChevronUpDownIcon
          className='h-5 w-5 text-gray-400'
          aria-hidden='true'
        />
      </div>
    </Popover.Button>
  );
}

function Inner({
  asset,
  onRemove,
}: {
  asset: Asset;
  onRemove: (asset: Asset) => void;
}) {
  invariant(asset, 'Asset must be defined');

  const deployment = asset.deployment;

  return (
    <div className='mb-1 flex items-center gap-2 rounded border border-outline-variant px-2.5 pb-2 pt-1.5 shadow-sm'>
      <div className='h-11 w-11'>
        <Avatar
          image={deployment?.image || asset.image}
          name={asset?.name}
          size='full'
          square
          className='rounded-md'
        />
      </div>
      <div className='ml-1 flex max-w-full flex-col items-start overflow-hidden text-base'>
        <p className='max-w-full truncate text-base font-medium text-grey-70 lg:text-[15px] lg:leading-5'>
          {asset.name}
        </p>
        <p className='truncate text-xs text-grey-40'>
          {deployment?.space.name}
        </p>
      </div>
      <div className='ml-auto'>
        <RemoveButton onClick={() => onRemove(asset)} />
      </div>
    </div>
  );
}

function OptionInner({ asset }: { asset: Asset }) {
  const deployment = asset.deployment;
  return (
    <>
      <div className='h-6 w-6'>
        <Avatar
          image={deployment?.image || asset.image}
          name={asset?.name}
          size='full-small-text'
          square
          className='rounded-md !text-base'
        />
      </div>
      <div className='flex min-w-0 flex-col'>
        <div className='flex items-center gap-2'>
          <span className='truncate'>{asset.name}</span> -{' '}
          <span className='truncate'>{asset.type}</span>
        </div>
        <span className='truncate text-grey-40'>{deployment?.space.name}</span>
      </div>
    </>
  );
}

function RemoveButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      <FontAwesomeIcon
        icon={faXmark as IconProp}
        className='-mb-1 h-5 w-5 stroke-1 text-secondary hover:text-primary active:text-primary'
      />
    </button>
  );
}
